import { store } from "store";
import { clearAuth } from "store/slices/authSlice";

const BASE_URL = "https://api.waaconnect.com/v1";
const CACHE_PREFIX = "waa_cache_";

interface CacheEntry<T> {
  data: T;
  updatedAt: number;
}

const memoryCache = new Map<string, CacheEntry<unknown>>();

function getCacheStorageKey(key: string) {
  return `${CACHE_PREFIX}${key}`;
}

export function readCachedData<T>(key: string): T | null {
  const fromMemory = memoryCache.get(key) as CacheEntry<T> | undefined;
  if (fromMemory) {
    return fromMemory.data;
  }

  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(getCacheStorageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed !== "object" || !("data" in parsed)) return null;
    memoryCache.set(key, parsed as CacheEntry<unknown>);
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeCachedData<T>(key: string, data: T) {
  const entry: CacheEntry<T> = {
    data,
    updatedAt: Date.now(),
  };

  memoryCache.set(key, entry as CacheEntry<unknown>);

  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(getCacheStorageKey(key), JSON.stringify(entry));
  } catch {
    // Ignore quota errors silently.
  }
}

export function clearCachedData(key: string) {
  memoryCache.delete(key);
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(getCacheStorageKey(key));
}

// ─── JWT utils ───────────────────────────────────────────────────────────────

interface JwtPayload {
  exp?: number;
  adminId?: string;
  email?: string;
  role?: string;
  iat?: number;
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload)) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  // exp est en secondes, Date.now() en millisecondes
  return Date.now() / 1000 >= payload.exp;
}

// ─── Session cleanup ──────────────────────────────────────────────────────────

function logout(reason: "expired" | "unauthorized") {
  store.dispatch(clearAuth());
  // La redirection est gérée par App.tsx via le selector Redux
  if (reason === "expired") {
    console.warn("[apiClient] Token expiré — déconnexion automatique.");
  }
}

// ─── Core request ─────────────────────────────────────────────────────────────

interface RequestOptions extends RequestInit {
  /** Passer `true` pour les endpoints publics (login, etc.) */
  skipAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (!skipAuth) {
    const { auth } = store.getState();
    const token = auth.token;

    if (!token) {
      logout("unauthorized");
      throw new Error("Non authentifié");
    }

    if (isTokenExpired(token)) {
      logout("expired");
      throw new Error("Session expirée");
    }

    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
  });

  // 401 côté serveur → forcer la déconnexion
  if (response.status === 401 && !skipAuth) {
    logout("unauthorized");
    throw new Error("Non autorisé");
  }

  let data: unknown;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      typeof data === "object" && data !== null && "message" in data
        ? (data as { message: string }).message
        : `Erreur ${response.status}`;
    throw new Error(message);
  }

  return data as T;
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export const apiClient = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body), ...opts }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),

  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
